/**
 * Swamithra — queries, their threads and their feedback.
 *
 * TWO VIEWS OF ONE RECORD, AND THEY ARE NOT THE SAME SHAPE. That is the whole
 * reason both live in this file rather than one schema being reused with a few
 * optional fields:
 *
 *   - the user's view NEVER carries `closeReason` (US-01-003.13)
 *   - the Expert's LIST view never carries the user's mobile number; only the
 *     detail view does (US-01-001.21)
 *
 * A shared schema with optional fields would satisfy the type checker and leak
 * on the first endpoint that forgot to strip a field. Two schemas mean the
 * parse fails loudly instead.
 */

import { z } from "zod"

import { attachmentSchema } from "./attachment"
import { queryCategorySchema } from "./categories"
import {
	FEEDBACK_COMMENT_MAX_LENGTH,
	QUERY_DESCRIPTION_MAX_LENGTH,
	QUERY_TITLE_MAX_LENGTH,
} from "./constants"
import {
	closeReasonSchema,
	feedbackRatingSchema,
	senderTypeSchema,
	userTypeSchema,
	workStatusSchema,
} from "./enums"
import { withReference } from "./ids"
import { e164Schema } from "./phone"
import { optionalResponseDraftSchema } from "./response-draft"
import { isRichTextEmpty, richTextSchema, visibleRichTextSchema } from "./rich-text"

/**
 * An RFC 3339 instant the service sends as `null` while it has not happened.
 *
 * `nullish`, not `nullable`: `answered_at` is null on an open query and absent
 * from the three panel responses that carry only the fields they changed. Both
 * mean the same thing to a screen, and a schema that distinguished them would
 * fail a parse over a field nothing renders.
 */
const nullableInstant = z
	.string()
	.nullish()
	.transform(value => value ?? null)

/**
 * Reconstructs `firstResponseAt` from the SLA pair the service does send.
 *
 * `elapsed_running: false` means the clock stopped, and it stopped when the
 * first response landed — `submitted_at + elapsed_seconds`. While it is still
 * running there is no first response and the value is null, which is exactly
 * what the elapsed column keys its neutral / warning / breach treatment off
 * (US-01-001.11/.12).
 */
function withElapsedFirstResponse(value: unknown): unknown {
	if (value === null || typeof value !== "object" || Array.isArray(value)) return value

	const record = value as Record<string, unknown>
	if ("firstResponseAt" in record) return record
	if (record.elapsedRunning !== false) return { ...record, firstResponseAt: null }

	const submittedAt =
		typeof record.submittedAt === "string" ? Date.parse(record.submittedAt) : NaN
	const elapsed = typeof record.elapsedSeconds === "number" ? record.elapsedSeconds : NaN
	if (Number.isNaN(submittedAt) || Number.isNaN(elapsed)) {
		return { ...record, firstResponseAt: null }
	}

	return { ...record, firstResponseAt: new Date(submittedAt + elapsed * 1000).toISOString() }
}

/** Gathers the panel detail's three flat user fields into one object. */
function withPanelUser(value: unknown): unknown {
	if (value === null || typeof value !== "object" || Array.isArray(value)) return value

	const record = value as Record<string, unknown>
	if (record.user !== undefined) return record

	return {
		...record,
		user: {
			fullName: record.userName,
			userType: record.userType,
			mobileNumber: record.userMobileNumber,
		},
	}
}

// -----------------------------------------------------------------------------
// Thread
// -----------------------------------------------------------------------------

/**
 * One message in a thread, from either side.
 *
 * The body is markup on the wire regardless of who wrote it: the Expert composes
 * rich text, the user composes plain text which the server escapes into the same
 * representation. One representation means one renderer, which is what makes
 * "renders identically in the Expert thread and in the user app"
 * (US-01-001.14) something the code guarantees rather than something two teams
 * have to remember.
 */
export const queryMessageSchema = z.object({
	id: z.string(),
	senderType: senderTypeSchema,
	/**
	 * ABSENT ON THE WIRE, and that is deliberate on the service's side: a thread
	 * shows "You" and "Expert", never which Expert, so no name is sent. Kept as
	 * an optional field rather than deleted because the bubble components read
	 * it, and `senderType` is what actually decides the alignment
	 * (US-00-002.31).
	 */
	senderName: z
		.string()
		.nullish()
		.transform(value => value ?? null),
	bodyRich: z.string(),
	attachments: z.array(attachmentSchema).default([]),
	createdAt: z.string(),
})

export type QueryMessage = z.infer<typeof queryMessageSchema>

// -----------------------------------------------------------------------------
// Feedback
// -----------------------------------------------------------------------------

/**
 * A rating already given.
 *
 * The service stamps it `created_at` and sends an empty string for "no comment";
 * this package has always called the instant `submittedAt` and treated an absent
 * comment as null, because a read-only summary that renders an empty paragraph
 * is not the same thing as one that renders nothing (US-00-002.14).
 */
export const queryFeedbackSchema = z.preprocess(
	value => {
		if (value === null || typeof value !== "object") return value
		const record = value as Record<string, unknown>
		return {
			...record,
			submittedAt: record.submittedAt ?? record.createdAt,
			comment: record.comment === "" ? null : record.comment,
		}
	},
	z.object({
		rating: feedbackRatingSchema,
		comment: z
			.string()
			.nullish()
			.transform(value => value ?? null),
		submittedAt: z.string(),
	}),
)

export type QueryFeedback = z.infer<typeof queryFeedbackSchema>

/**
 * The 'Was this helpful?' submission.
 *
 * The comment limit is enforced three times over: a live counter blocks input,
 * this schema blocks the request, and the server returns 422 (US-00-002.15).
 * Blocking input is the only one the user should ever meet.
 */
export const queryFeedbackInputSchema = z.object({
	rating: feedbackRatingSchema,
	comment: z
		.string()
		.trim()
		.max(
			FEEDBACK_COMMENT_MAX_LENGTH,
			`Keep your comment under ${FEEDBACK_COMMENT_MAX_LENGTH} characters.`,
		)
		.optional(),
})

export type QueryFeedbackInput = z.infer<typeof queryFeedbackInputSchema>

// -----------------------------------------------------------------------------
// The user's view
// -----------------------------------------------------------------------------

const querySummaryFieldsSchema = z.object({
	id: z.string(),
	/** As the service rendered it — `#12`, the same string in all four places. */
	reference: z.string(),
	/** The number inside the reference, derived by `withReference`. */
	sequence: z.number().int().positive(),
	title: z.string(),
	category: queryCategorySchema,
	status: workStatusSchema,
	submittedAt: z.string(),
	lastUpdatedAt: z.string(),
	answeredAt: nullableInstant,
	closedAt: nullableInstant,
	/** Activity this viewer's side has not opened (NOTASK-1). Older servers omit it. */
	hasUnseen: z.boolean().default(false),
	/** How many changes this viewer's side has not opened; every change counts. */
	unseenCount: z.number().int().nonnegative().default(0),
})

export const querySummarySchema = z.preprocess(withReference, querySummaryFieldsSchema)

export type QuerySummary = z.output<typeof querySummarySchema>

/**
 * The user's detail view.
 *
 * `closedAt` is here and `closeReason` is not — that omission IS US-01-003.13,
 * and a parse of a response that included one would simply drop it.
 *
 * `feedbackDue` and `canFollowUp` are the SERVER'S ANSWERS to two questions each
 * of the three clients would otherwise answer for itself: whether to raise the
 * 'Was this helpful?' prompt (US-00-002.13) and whether to render the follow-up
 * composer at all (US-00-002.12). Three copies of "status === answered &&
 * feedback === null" is three chances to disagree with the 409 the API returns.
 */
export const queryDetailSchema = z.preprocess(
	withReference,
	querySummaryFieldsSchema.extend({
		description: z.string(),
		attachments: z.array(attachmentSchema).default([]),
		messages: z.array(queryMessageSchema).default([]),
		feedback: queryFeedbackSchema.nullish().transform(value => value ?? null),
		feedbackDue: z.boolean().default(false),
		canFollowUp: z.boolean().default(true),
	}),
)

export type QueryDetail = z.output<typeof queryDetailSchema>

// -----------------------------------------------------------------------------
// The Expert's view
// -----------------------------------------------------------------------------

/**
 * The state of one query as the panel sees it, with no user attached.
 *
 * This is what 'Mark as Answered', 'Close Query' and a category correction
 * answer with: the query's own fields, and `user_name` / `user_type` left empty
 * because the caller is already looking at the record they acted on. Parsing
 * those three responses as a full row failed on an empty user type — which is
 * why the row below adds the user rather than this schema requiring one.
 *
 * NO `firstResponseAt` FIELD EXISTS ON THE WIRE. The SLA column is served as
 * `elapsed_seconds` plus `elapsed_running`: time since submission while a query
 * is unanswered, the ACHIEVED first-response duration once it is not
 * (US-01-001.11/.12). `firstResponseAt` is reconstructed from the pair below,
 * because that instant is exactly `submitted_at + elapsed_seconds` on a query
 * whose clock has stopped.
 */
const expertQueryStateFieldsSchema = z.object({
	id: z.string(),
	reference: z.string(),
	sequence: z.number().int().positive(),
	/**
	 * The owner's own count (`#3`), the number the user app shows and the user
	 * quotes on a call. Unique only per user: `reference` stays the panel's id.
	 */
	userReference: z.string().default(""),
	title: z.string(),
	category: queryCategorySchema,
	status: workStatusSchema,
	submittedAt: z.string(),
	answeredAt: nullableInstant,
	closedAt: nullableInstant,
	/** Panel and reporting only (US-01-003.13). */
	closeReason: closeReasonSchema.nullish().transform(value => value ?? null),
	/** Seconds on the SLA clock — outstanding, or achieved. */
	elapsedSeconds: z.number().int().nonnegative().default(0),
	/** False once the first response landed and the clock stopped. */
	elapsedRunning: z.boolean().default(true),
	/** Derived, not sent: null while the clock is still running. */
	firstResponseAt: nullableInstant,
	/** Activity this viewer's side has not opened (NOTASK-1). Older servers omit it. */
	hasUnseen: z.boolean().default(false),
	/** How many changes this viewer's side has not opened; every change counts. */
	unseenCount: z.number().int().nonnegative().default(0),
})

export const expertQueryStateSchema = z.preprocess(
	value => withElapsedFirstResponse(withReference(value)),
	expertQueryStateFieldsSchema,
)

export type ExpertQueryState = z.output<typeof expertQueryStateSchema>

/**
 * A dashboard row.
 *
 * NO MOBILE NUMBER. It is searchable and it appears on the detail view, but a
 * list of rows is a list of numbers waiting to be screenshotted
 * (US-01-001.21).
 */
export const expertQueryRowSchema = z.preprocess(
	value => withElapsedFirstResponse(withReference(value)),
	expertQueryStateFieldsSchema.extend({
		userName: z.string(),
		userType: userTypeSchema,
	}),
)

export type ExpertQueryRow = z.output<typeof expertQueryRowSchema>

/**
 * The one screen in the product that shows a user's number (US-01-001.5).
 *
 * The service sends it flat, as `user_mobile_number`, alongside the `user_name`
 * and `user_type` the row already carries. It is gathered into a `user` object
 * here so the detail screen reads one thing rather than three fields that happen
 * to share a prefix. There is no user ID in the payload and none is wanted: no
 * screen in the panel navigates to a user.
 */
export const expertQueryDetailSchema = z.preprocess(
	value => withPanelUser(withElapsedFirstResponse(withReference(value))),
	expertQueryStateFieldsSchema.extend({
		userName: z.string(),
		userType: userTypeSchema,
		description: z.string(),
		attachments: z.array(attachmentSchema).default([]),
		messages: z.array(queryMessageSchema).default([]),
		user: z.object({
			fullName: z.string(),
			userType: userTypeSchema,
			mobileNumber: e164Schema,
		}),
		feedback: queryFeedbackSchema.nullish().transform(value => value ?? null),
		/** The Experts' saved, unsent reply, or null. */
		responseDraft: optionalResponseDraftSchema,
	}),
)

export type ExpertQueryDetail = z.output<typeof expertQueryDetailSchema>

// -----------------------------------------------------------------------------
// Inputs
// -----------------------------------------------------------------------------

export const createQueryInputSchema = z.object({
	title: z
		.string()
		.trim()
		.min(1, "Give your query a title.")
		.max(QUERY_TITLE_MAX_LENGTH, "That title is too long."),
	/**
	 * Rich-text markup, same contract as `body`/`notes` below. Not `.trim()`-ed:
	 * trimming a markup string risks clipping at a tag boundary. The limit is
	 * on the characters the user sees (the form's counter), with the markup
	 * itself held to `RICH_TEXT_MAX_LENGTH`, as the server does.
	 */
	description: visibleRichTextSchema(
		QUERY_DESCRIPTION_MAX_LENGTH,
		`Keep your description to ${QUERY_DESCRIPTION_MAX_LENGTH} characters or fewer.`,
	).refine(value => !isRichTextEmpty(value), "Describe what you need help with."),
})

export type CreateQueryInput = z.infer<typeof createQueryInputSchema>

/**
 * A user's follow-up. `body` is rich-text markup, same contract as an Expert's
 * `bodyRich` below — a composer with no formatting UI can still send this, since
 * plain text with no tags is well-formed markup with no formatting. Attachments
 * travel alongside as files.
 */
export const followUpInputSchema = z.object({
	body: richTextSchema,
})

export type FollowUpInput = z.infer<typeof followUpInputSchema>

/** An Expert's reply. Rich text, and no file-upload control in Beta (US-01-001.26). */
export const expertResponseInputSchema = z.object({
	bodyRich: richTextSchema,
})

export type ExpertResponseInput = z.infer<typeof expertResponseInputSchema>

/**
 * The ONLY user-supplied field an Expert may change (US-01-001.19).
 *
 * `.strict()` so an attempt to smuggle a title or a description through the same
 * endpoint fails before it is sent, matching the server's 403.
 */
export const expertCategoryUpdateSchema = z
	.object({
		category: queryCategorySchema,
	})
	.strict()

// -----------------------------------------------------------------------------
// List parameters
// -----------------------------------------------------------------------------

/** The user's own list. Ownership is resolved from the session, never from a parameter. */
export type MyQueriesFilters = {
	status?: z.infer<typeof workStatusSchema>
}

/**
 * Sortable columns on the Expert dashboard (US-01-001.4).
 *
 * A closed set, because sorting has to be indexed server-side and because
 * "rows with no answeredAt sort last in both directions" is a rule the server
 * applies per column rather than a generic nulls-last setting.
 */
export const expertQuerySortSchema = z.enum([
	"submittedAt",
	"-submittedAt",
	"answeredAt",
	"-answeredAt",
])

export type ExpertQuerySort = z.infer<typeof expertQuerySortSchema>

export type ExpertQueryFilters = {
	status?: z.infer<typeof workStatusSchema>
	category?: z.infer<typeof queryCategorySchema>
	/** IST calendar dates, inclusive — the panel's Date Range control. */
	submittedFrom?: string
	submittedTo?: string
	/** Name, title, `#12`, or a mobile number in any format (US-01-001.3). */
	search?: string
	sort?: ExpertQuerySort
}
